import { resolveTenant, type Tenant } from "./tenant";
import { useTenantStore } from "@/stores/tenant";

/** #RRGGBB → "H S% L%" */
function hexToHSL(hex: string): string | null {
  const m = hex.replace("#", "").match(/^([0-9a-f]{6})$/i);
  if (!m) return null;
  const r = parseInt(m[1].slice(0, 2), 16) / 255;
  const g = parseInt(m[1].slice(2, 4), 16) / 255;
  const b = parseInt(m[1].slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/** "H S% L%" → partes numéricas */
function partes(hsl: string) {
  const [h, s, l] = hsl.split(" ");
  return { h: parseFloat(h), s: parseFloat(s), l: parseFloat(l) };
}

/**
 * A cor da marca costuma ser escura (navy #0E143A) para funcionar sobre fundo claro.
 * No tema escuro ela sumiria contra o fundo, então clareamos mantendo o MATIZ da marca,
 * do mesmo jeito que os tokens do design fazem (navy 14% → periwinkle 62%).
 */
function paraTema(hsl: string, dark: boolean) {
  const { h, s, l } = partes(hsl);
  if (!dark) return { cor: `${h} ${s}% ${l}%`, sobre: l > 62 ? "222 47% 11%" : "0 0% 100%" };
  const nl = Math.min(Math.max(l, 60), 72);        // garante leitura sobre o fundo escuro
  const ns = Math.min(s, 72);                      // evita saturação berrante
  return { cor: `${h} ${ns}% ${nl}%`, sobre: "230 45% 8%" };
}

let marcaAtual: Tenant | null = null;

/** (Re)aplica as cores da marca respeitando o tema vigente. */
function aplicarCores(t: Tenant) {
  const root = document.documentElement;
  const dark = root.classList.contains("dark");

  const pri = t.primary_color ? hexToHSL(t.primary_color) : null;
  if (pri) {
    const { cor, sobre } = paraTema(pri, dark);
    root.style.setProperty("--primary", cor);
    root.style.setProperty("--primary-foreground", sobre);
    root.style.setProperty("--ring", cor);
  } else {
    // sem marca: devolve o controle aos tokens do CSS
    root.style.removeProperty("--primary");
    root.style.removeProperty("--primary-foreground");
    root.style.removeProperty("--ring");
  }

  const acc = t.accent_color ? hexToHSL(t.accent_color) : null;
  if (acc) {
    const { cor, sobre } = paraTema(acc, dark);
    root.style.setProperty("--accent", cor);
    root.style.setProperty("--accent-foreground", sobre);
    root.style.setProperty("--destructive", cor);
    root.style.setProperty("--destructive-foreground", sobre);
  } else {
    root.style.removeProperty("--accent");
    root.style.removeProperty("--accent-foreground");
    root.style.removeProperty("--destructive");
    root.style.removeProperty("--destructive-foreground");
  }

  // o rail lateral é sempre navy da marca, nos dois temas: não mexer
  if (t.sidebar_bg) { const h = hexToHSL(t.sidebar_bg); if (h) root.style.setProperty("--sidebar-bg", h); }
  if (t.sidebar_fg) { const h = hexToHSL(t.sidebar_fg); if (h) root.style.setProperty("--sidebar-fg", h); }
}

/** Reaplica a marca quando o tema muda (o toggle mexe na classe .dark do <html>). */
export function refreshBranding() {
  if (marcaAtual) aplicarCores(marcaAtual);
}

let observando = false;
function observarTema() {
  if (observando) return;
  observando = true;
  new MutationObserver(() => refreshBranding())
    .observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
}

export function applyBranding(t: Tenant) {
  marcaAtual = t;
  const root = document.documentElement;

  // Tema primeiro: as cores da marca dependem dele
  const prefersDark = matchMedia("(prefers-color-scheme: dark)").matches;
  const wantDark = t.tema_padrao === "dark" || (t.tema_padrao === "auto" && prefersDark);
  root.classList.toggle("dark", wantDark);

  aplicarCores(t);
  observarTema();

  document.title = t.nome_fantasia || "Aferi";
}

export async function bootBranding() {
  const tenant = await resolveTenant();
  applyBranding(tenant);
  useTenantStore().set(tenant);
}
